import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { clearModuleAccessCache, prefetchTenantModuleAccess } from "../../../shared/utils/moduleAccessStore";
import {
  checkTenantSubscriptionActive,
  clearSubscriptionBlocked,
} from "../../../shared/utils/subscriptionAccessStore";
import {
  clearStoredAccessToken,
  getRefreshInFlight,
  getStoredAccessToken,
  setRefreshInFlight,
  setStoredAccessToken,
} from "./accessTokenStore";

const API_URL = process.env.REACT_APP_API_URL;

axios.defaults.withCredentials = true;

let sessionRestorePromise = null;

const AuthService = {
  getToken() {
    return getStoredAccessToken();
  },

  setToken(token) {
    setStoredAccessToken(token);
    if (token) {
      axios.defaults.headers.common.Authorization = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common.Authorization;
    }
  },

  removeToken() {
    clearStoredAccessToken();
    delete axios.defaults.headers.common.Authorization;
  },

  getUser() {
    const token = this.getToken();
    if (!token) return null;
    try {
      return jwtDecode(token);
    } catch (error) {
      console.error("Error decoding token:", error);
      return null;
    }
  },

  buildUserDetailFromToken(token) {
    const user = jwtDecode(token);
    return {
      username: user.UserName,
      UserEmail: user.UserEmail,
      UserId: user.UserId,
      UserRoleId: user.UserRoleId,
      ClientId: user.ClientId,
    };
  },

  setSessionFromAccessToken(accessToken) {
    if (!accessToken) return;
    setStoredAccessToken(accessToken);
    axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("token");
    localStorage.removeItem("user");
  },

  /** Restore session on page load via httpOnly refresh cookie. */
  async restoreSession() {
    if (sessionRestorePromise) return sessionRestorePromise;
    sessionRestorePromise = this._restoreSessionOnce();
    return sessionRestorePromise;
  },

  async _restoreSessionOnce() {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("token");
    localStorage.removeItem("user");

    if (this.getToken() && !this.isTokenExpired(this.getToken())) {
      return true;
    }

    try {
      const response = await this.refreshTokens();
      const token = response?.data?.newAccessToken;
      if (!token) return false;
      this.setSessionFromAccessToken(token);
      await prefetchTenantModuleAccess().catch(() => {});
      await checkTenantSubscriptionActive().catch(() => true);
      return true;
    } catch {
      this.removeToken();
      return false;
    }
  },

  async login(username, password, rememberMe, navigate) {
    const loginPayload = { email: username, password };
    try {
      const response = await axios.post(`${API_URL}/Login/Login`, loginPayload, {
        withCredentials: true,
      });
      const token = response.data.accessToken;
      if (!token) throw new Error("No access token returned");

      this.setSessionFromAccessToken(token);
      await prefetchTenantModuleAccess().catch(() => {});
      const subActive = await checkTenantSubscriptionActive().catch(() => true);

      if (rememberMe) {
        this.setRememberedCredentials(username);
      } else {
        this.clearRememberedCredentials();
      }

      navigate(subActive ? "/dashboard" : "/subscription-inactive");
    } catch (err) {
      console.error("Login failed", err);
      throw err;
    }
  },

  async performLogout(navigate) {
    const userId = this.getLoggedInUserId();
    const token = this.getToken();
    try {
      if (userId && token) {
        await axios.put(`${API_URL}/Login/LogOut/${userId}`, null, {
          withCredentials: true,
          headers: { Authorization: `Bearer ${token}` },
        });
      }
    } catch (error) {
      console.warn("Server logout failed; clearing local session anyway.", error);
    }

    sessionRestorePromise = null;
    clearModuleAccessCache();
    clearSubscriptionBlocked();
    this.removeToken();
    localStorage.removeItem("hasShownPopup");
    localStorage.removeItem("lastActivityTime");

    if (typeof navigate === "function") {
      navigate("/login", { replace: true });
    } else if (typeof window !== "undefined") {
      window.location.assign(`${window.location.origin}/login`);
    }
  },

  logout() {
    this.performLogout(null);
  },

  /** JWT-derived profile (not tamperable localStorage). */
  getCurrentUser() {
    const token = this.getToken();
    if (!token || this.isTokenExpired(token)) return null;
    try {
      return this.buildUserDetailFromToken(token);
    } catch {
      return null;
    }
  },

  isAuthenticated() {
    const token = this.getToken();
    return !!token && !this.isTokenExpired(token);
  },

  _jwtClaims() {
    return this.getUser() || {};
  },

  getLoggedInUserId() {
    const claims = this._jwtClaims();
    const id = claims.UserId ?? claims.userId;
    return id != null ? parseInt(id, 10) : null;
  },

  getLoggedInRoleId() {
    const claims = this._jwtClaims();
    const role = claims.UserRoleId ?? claims.userRoleId;
    return role != null ? parseInt(role, 10) : null;
  },

  getLoggedInClientId() {
    const claims = this._jwtClaims();
    const client = claims.ClientId ?? claims.clientId;
    return client != null ? parseInt(client, 10) : null;
  },

  /**
   * Employees always use their JWT user id.
   * Admin/SuperAdmin may pass ?id= for team views (backend enforces tenant).
   */
  resolveScopedUserId(queryUserId) {
    const jwtId = this.getLoggedInUserId();
    if (!jwtId) return null;
    const roleId = this.getLoggedInRoleId();
    if (roleId === 4) return jwtId;
    if (queryUserId == null || queryUserId === "") return jwtId;
    const parsed = parseInt(queryUserId, 10);
    return Number.isNaN(parsed) ? jwtId : parsed;
  },

  async getLastLoginTime() {
    const userId = this.getLoggedInUserId();
    if (!userId) throw new Error("User ID is not available");
    try {
      const response = await axios.get(`${API_URL}/User/lastlogintime/${userId}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching last login time", error);
      throw error;
    }
  },

  setRememberedCredentials(email) {
    localStorage.setItem("email", email);
    localStorage.removeItem("password");
  },

  getRememberedCredentials() {
    return { email: localStorage.getItem("email"), password: "" };
  },

  clearRememberedCredentials() {
    localStorage.removeItem("email");
    localStorage.removeItem("password");
  },

  handleLogout() {
    this.performLogout(null);
  },

  async refreshTokens() {
    let inFlight = getRefreshInFlight();
    if (inFlight) return inFlight;

    inFlight = axios.post(
      `${API_URL}/Login/RefreshTokens`,
      {},
      {
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
      }
    );
    setRefreshInFlight(inFlight);

    try {
      return await inFlight;
    } finally {
      setRefreshInFlight(null);
    }
  },

  isTokenExpired(token) {
    if (!token) return false;
    try {
      const decodedToken = jwtDecode(token);
      return decodedToken.exp < Date.now() / 1000;
    } catch {
      return true;
    }
  },

  prepareAuthenticatedRequest(config) {
    const token = this.getToken();
    if (!token) return config;
    if (this.isTokenExpired(token)) {
      this.logout();
      return Promise.reject(new axios.Cancel("Session expired"));
    }
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
    config.withCredentials = true;
    return config;
  },

  getUserImageById: async (userId) => {
    const response = await axios.get(`${API_URL}/User/GetUserImage/${userId}`);
    return response.data;
  },

  getAgencyLogoByClientId: async (clientId) => {
    const response = await axios.get(`${API_URL}/Client/GetAgencyLogoByClientId/${clientId}`);
    return response.data;
  },

  getAgencyLogoAndNameByClientId: async (clientId) => {
    const response = await axios.get(`${API_URL}/Client/GetAgencyLogoAndNameByClientId/${clientId}`);
    const data = response.data;
    return {
      agencyName: data?.agencyName ?? data?.AgencyName ?? "",
      agencyLogo: data?.agencyLogo ?? data?.AgencyLogo ?? "",
    };
  },
};

export default AuthService;
